/* Pure date helpers for the booking range picker.
 *
 * Everything is an ISO "YYYY-MM-DD" STRING, and every calculation goes through Date.UTC. A local
 * Date would shift the day for anyone west of UTC — a guest in Jakarta picking the 3rd would send
 * the 2nd — and the value has to survive a round trip to Rails, which speaks ISO dates.
 */

export const toISO = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export const parseISO = (iso) => {
  const [year, month, day] = String(iso).split("-").map(Number);
  return { year, month: month - 1, day };
};

const utc = (iso) => {
  const { year, month, day } = parseISO(iso);
  return Date.UTC(year, month, day);
};

export const addDays = (iso, amount) => {
  const date = new Date(utc(iso));
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
};

export const nightsBetween = (from, to) =>
  Math.round((utc(to) - utc(from)) / 86400000);

export const isBefore = (a, b) => utc(a) < utc(b);
export const isAfter = (a, b) => utc(a) > utc(b);
export const isSame = (a, b) => a === b;
export const isWithin = (iso, from, to) =>
  utc(iso) > utc(from) && utc(iso) < utc(to);

export const addMonths = (iso, amount) => {
  const { year, month } = parseISO(iso);
  const shifted = new Date(Date.UTC(year, month + amount, 1));
  return shifted.toISOString().slice(0, 10);
};

/* Six rows of seven, always — a calendar that changes height as you page between months makes the
 * whole panel jump under the cursor.
 *
 * Week starts MONDAY. Both locales this site serves (id-ID and en-GB) start there; only en-US
 * starts on Sunday, and the property is Indonesian.
 */
export const monthGrid = (anchorISO) => {
  const { year, month } = parseISO(anchorISO);
  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  const offset = (firstOfMonth.getUTCDay() + 6) % 7; // Sunday=0 -> Monday=0
  const start = new Date(Date.UTC(year, month, 1 - offset));

  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => {
      const cell = new Date(start);
      cell.setUTCDate(start.getUTCDate() + week * 7 + day);
      const iso = cell.toISOString().slice(0, 10);
      return {
        iso,
        inMonth: cell.getUTCMonth() === month,
        day: cell.getUTCDate(),
      };
    }),
  );
};

const localeTag = (language) => (language === "id" ? "id-ID" : "en-GB");

/* timeZone: "UTC" on every formatter — the ISO string is already the calendar day we mean, so
 * letting the formatter reinterpret it in the viewer's zone would print the day before. */
export const formatLong = (iso, language) =>
  new Intl.DateTimeFormat(localeTag(language), {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(utc(iso)));

/* What the two trigger slots show. No year: two long dates side by side truncate on a phone, and a
 * booking inside the next twelve months is never ambiguous without it. The full date, year and all,
 * is still on every day cell's aria-label. */
export const formatTrigger = (iso, language) =>
  new Intl.DateTimeFormat(localeTag(language), {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(utc(iso)));

export const formatShort = (iso, language) =>
  new Intl.DateTimeFormat(localeTag(language), {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(utc(iso)));

export const formatMonth = (iso, language) =>
  new Intl.DateTimeFormat(localeTag(language), {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(utc(iso)));

export const weekdayNames = (language) => {
  const formatter = new Intl.DateTimeFormat(localeTag(language), {
    weekday: "short",
    timeZone: "UTC",
  });
  // 2024-01-01 was a Monday, which is where our week starts.
  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(new Date(Date.UTC(2024, 0, 1 + index))),
  );
};
